import { createContext, useState, useContext, useEffect, useCallback, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import type { Agency } from '../../utils/constant';
import useHttp from '../../network/UseHttp';

export type { Agency };

export interface AgencyContextValue {
    agency: Agency | null;
    setAgency: (agency: Agency | null) => void;
    agencies: Agency[];
    getAgencyByName: (agencyName: string) => Agency | undefined;
    agenciesLoading: boolean;
    agenciesError: unknown;
};

const RESERVED_PATH_SEGMENTS = ['', 'payment', 'payment-success'];

/** Agency context: loads partner agencies only when agencyName is in the URL (api/admin/get-partner-agencies?agencyName=...).
 * When there is no agency in the URL, the API is not called and agencies/agency are cleared so agency data is not exposed.
 */
export const AgencyContext = createContext<AgencyContextValue | null>(null);

interface AgencyProviderProps {
    children: ReactNode;
};

function getAgencyNameFromPathname(pathname: string): string | null {
    const segment = pathname.replace(/^\/|\/$/g, '').split('/')[0] ?? '';
    return segment && !RESERVED_PATH_SEGMENTS.includes(segment)
        ? segment : null;
};

export const AgencyProvider = ({ children }: AgencyProviderProps) => {
    const [agency, setAgency] = useState<Agency | null>(null);
    const [agencies, setAgencies] = useState<Agency[]>([]);
    const [agenciesLoading, agenciesError, sendHttpRequest] = useHttp();
    const { pathname } = useLocation();
    const agencyNameFromUrl = getAgencyNameFromPathname(pathname);
    
    useEffect(() => {
        if (!agencyNameFromUrl) {
            setAgencies([]);
            setAgency(null);
            return;
        }
        const url = `api/admin/get-partner-agencies?agencyName=${encodeURIComponent(agencyNameFromUrl)}`;
        sendHttpRequest(url, 'GET', null, (data: unknown) => {
            const raw = data as
                | Agency[]
                | { agencies?: Agency[]; data?: Agency[] };
            const list = Array.isArray(raw)
                ? raw
                : raw?.agencies ?? raw?.data ?? [];
            setAgencies(list);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only fetch when agencyName from URL is present
    }, [agencyNameFromUrl]);
    
    const getAgencyByName = useCallback(
        (agencyName: string): Agency | undefined => {
            if (!agencyName || typeof agencyName !== 'string') return undefined;
            return agencies.find((a) =>
                a.agencyName?.toLowerCase() === agencyName?.toLowerCase()
            );
        },
        [agencies]
    );
    
    const value: AgencyContextValue = {
        agency,
        setAgency,
        agencies,
        getAgencyByName,
        agenciesLoading,
        agenciesError,
    };
    
    return (
        <AgencyContext.Provider value={value}>
            {children}
        </AgencyContext.Provider>
    );
};

/** Use to get current agency, agencies list, getAgencyByName, and loading/error state. */
export function useAgency(): AgencyContextValue {
    const context = useContext(AgencyContext);
    if (context == null) {
        throw new Error('useAgency must be used within an AgencyProvider');
    }
    return context;
};